import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, Mail, Send, Users, Trash2, RefreshCw,
  LogOut, Eye, EyeOff, Loader2, CheckCircle,
  AlertCircle, ChevronDown, ChevronUp, Sparkles,
  Clock, AtSign, FileText
} from 'lucide-react';
import { getSubscribers, unsubscribe, type Subscriber } from '@/lib/supabase';
import { springPresets } from '@/lib/motion';
import { toast } from 'sonner';

// ── 發信 API（呼叫 Supabase Edge Function）──────────────
async function sendNewsletter(payload: {
  subject: string;
  htmlBody: string;
  senderName: string;
  senderEmail: string;
  recipientEmails: string[];
}) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const res = await fetch(`${supabaseUrl}/functions/v1/send-newsletter`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseKey}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

// ── 管理頁密碼（可自行修改）─────────────────────────────
const ADMIN_PASSWORD = 'tracy2024';

// ===================== LoginGate =====================
const LoginGate: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd === ADMIN_PASSWORD) {
      sessionStorage.setItem('admin_auth', '1');
      onLogin();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#f8fafc' }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springPresets.gentle}
        className="w-full max-w-sm"
      >
        <div className="rounded-3xl p-8 bg-white border border-gray-100 shadow-lg shadow-gray-100/50">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg, #a855f7, #f97316)', boxShadow: '0 8px 20px rgba(168,85,247,0.3)' }}>
              <Lock size={24} className="text-white" />
            </div>
            <h1 className="text-xl font-black text-gray-900">管理員登入</h1>
            <p className="text-sm text-gray-400 mt-1">Tracy AI 小妹 電子報後台</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                placeholder="輸入管理員密碼"
                className="w-full px-4 py-3 pr-10 rounded-2xl text-sm text-gray-800 outline-none transition-all"
                style={{
                  background: '#f8fafc',
                  border: `1.5px solid ${error ? '#f87171' : '#e2e8f0'}`,
                  boxShadow: error ? '0 0 0 3px rgba(248,113,113,0.15)' : 'none',
                }}
                onFocus={e => { if (!error) { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.12)'; } }}
                onBlur={e => { if (!error) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; } }}
              />
              <button type="button" onClick={() => setShow(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <AnimatePresence>
              {error && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle size={12} /> 密碼錯誤，請再試一次
                </motion.p>
              )}
            </AnimatePresence>
            <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              className="w-full py-3 rounded-2xl text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #a855f7, #f97316)', boxShadow: '0 4px 16px rgba(168,85,247,0.3)' }}>
              進入後台
            </motion.button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// ===================== 訂閱者列表 =====================
const SubscriberTable: React.FC<{
  subscribers: Subscriber[];
  onUnsubscribe: (id: string) => void;
  loading: boolean;
}> = ({ subscribers, onUnsubscribe, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? subscribers : subscribers.slice(0, 8);

  if (loading) return (
    <div className="flex items-center justify-center py-12 text-gray-400">
      <Loader2 size={20} className="animate-spin mr-2" /> 載入中...
    </div>
  );
  if (subscribers.length === 0) return (
    <div className="text-center py-12 text-gray-400">
      <Users size={32} className="mx-auto mb-2 opacity-40" />
      <p className="text-sm">還沒有訂閱者</p>
    </div>
  );

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">#</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 hidden sm:table-cell">稱呼</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 hidden md:table-cell">訂閱時間</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((sub, i) => (
              <tr key={sub.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3 text-xs text-gray-400 font-mono">{i + 1}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold text-purple-600"
                      style={{ background: 'rgba(168,85,247,0.1)' }}>
                      {sub.email.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-gray-800 text-xs">{sub.email}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{sub.name ?? '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-400 hidden md:table-cell">
                  {new Date(sub.subscribed_at).toLocaleDateString('zh-TW', { year: 'numeric', month: 'short', day: 'numeric' })}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      if (confirm(`確定要移除 ${sub.email} 的訂閱嗎？`)) onUnsubscribe(sub.id);
                    }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {subscribers.length > 8 && (
        <button onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-1 mx-auto mt-3 text-xs text-purple-500 hover:text-purple-700 transition-colors">
          {expanded ? <><ChevronUp size={13} />收起</> : <><ChevronDown size={13} />查看全部 {subscribers.length} 位</>}
        </button>
      )}
    </div>
  );
};

// ===================== 郵件編輯器 =====================
const EmailEditor: React.FC<{
  subscribers: Subscriber[];
  disabled: boolean;
}> = ({ subscribers, disabled }) => {
  const [subject, setSubject] = useState('');
  const [senderName, setSenderName] = useState('Tracy AI 小妹');
  const [senderEmail, setSenderEmail] = useState('onboarding@resend.dev');
  const [body, setBody] = useState(EMAIL_TEMPLATE);
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const activeEmails = subscribers.map(s => s.email);

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('請填寫郵件主旨'); return; }
    if (!body.trim()) { toast.error('請填寫郵件內容'); return; }
    if (!senderEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderEmail)) {
      toast.error('請填寫有效的寄件者 Email'); return;
    }
    if (activeEmails.length === 0) { toast.error('目前沒有訂閱者可以發送'); return; }
    if (!confirm(`確定要發送郵件給 ${activeEmails.length} 位訂閱者嗎？`)) return;

    setSending(true);
    try {
      await sendNewsletter({
        subject: subject.trim(),
        htmlBody: body,
        senderName: senderName.trim() || 'Tracy AI 小妹',
        senderEmail: senderEmail.trim(),
        recipientEmails: activeEmails,
      });
      setSent(true);
      toast.success(`成功發送給 ${activeEmails.length} 位訂閱者！`, {
        description: `主旨：${subject}`,
      });
    } catch (err: unknown) {
      toast.error('發送失敗：' + (err instanceof Error ? err.message : '請檢查設定'));
    } finally {
      setSending(false);
    }
  };

  const InputStyle: React.CSSProperties = {
    background: '#fff',
    border: '1.5px solid #e2e8f0',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  return (
    <div className="space-y-4">
      {/* Sender info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1.5 block">寄件者名稱</label>
          <input value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="Tracy AI 小妹"
            className="w-full px-4 py-2.5 rounded-2xl text-sm text-gray-800 outline-none"
            style={InputStyle}
            onFocus={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1.5 block">寄件者 Email</label>
          <input value={senderEmail} onChange={e => setSenderEmail(e.target.value)}
            placeholder="your@domain.com"
            className="w-full px-4 py-2.5 rounded-2xl text-sm text-gray-800 outline-none"
            style={InputStyle}
            onFocus={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }} />
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="text-xs font-semibold text-gray-600 mb-1.5 block">郵件主旨 *</label>
        <input value={subject} onChange={e => setSubject(e.target.value)}
          placeholder="例：本週最值得關注的 5 個 AI 工具 🔥"
          className="w-full px-4 py-2.5 rounded-2xl text-sm text-gray-800 outline-none"
          style={InputStyle}
          onFocus={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }} />
        <p className="text-xs text-gray-400 mt-1">{subject.length} 字元（建議 30–60 字元）</p>
      </div>

      {/* Body */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-gray-600">郵件內容（HTML 格式）*</label>
          <div className="flex gap-2">
            <button onClick={() => setBody(EMAIL_TEMPLATE)}
              className="text-xs text-purple-500 hover:text-purple-700 transition-colors flex items-center gap-1">
              <Sparkles size={11} /> 套用範本
            </button>
            <button onClick={() => setPreview(v => !v)}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1">
              <Eye size={11} /> {preview ? '編輯' : '預覽'}
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {preview ? (
            <motion.div key="preview"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="rounded-2xl overflow-hidden border border-gray-100"
            >
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 text-xs text-gray-500">
                <Eye size={12} /> 預覽模式（實際收件人看到的樣子）
              </div>
              <div className="bg-white p-4 min-h-[300px] max-h-[500px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: body }} />
            </motion.div>
          ) : (
            <motion.textarea key="editor"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={14}
              placeholder="輸入 HTML 格式的郵件內容..."
              className="w-full px-4 py-3 rounded-2xl text-sm text-gray-800 font-mono outline-none resize-none"
              style={{ ...InputStyle, lineHeight: '1.6' }}
              onFocus={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Send bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
            style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)' }}>
            <Users size={13} className="text-purple-500" />
            <span className="font-semibold text-purple-600">{activeEmails.length}</span>
            <span className="text-xs text-purple-500">位訂閱者</span>
          </div>
          {sent && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle size={12} /> 已發送
            </span>
          )}
        </div>
        <motion.button
          onClick={handleSend}
          disabled={sending || disabled || activeEmails.length === 0}
          whileHover={{ scale: sending ? 1 : 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #a855f7, #f97316)', boxShadow: '0 4px 16px rgba(168,85,247,0.3)' }}
        >
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          {sending ? '發送中...' : `發送給全部 ${activeEmails.length} 位`}
        </motion.button>
      </div>
    </div>
  );
};

// ── 預設郵件範本 ─────────────────────────────────────────
const EMAIL_TEMPLATE = `<!DOCTYPE html>
<html lang="zh-TW">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Tracy AI 小妹電子報</title></head>
<body style="margin:0;padding:0;background:#f8fafc;font-family:'Helvetica Neue',Arial,sans-serif;">
<div style="max-width:600px;margin:0 auto;padding:24px 16px;">

  <!-- Header -->
  <div style="background:linear-gradient(135deg,#a855f7,#f97316);border-radius:20px;padding:32px 28px;margin-bottom:20px;text-align:center;">
    <div style="width:56px;height:56px;background:rgba(255,255,255,0.2);border-radius:16px;margin:0 auto 12px;display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:900;color:#fff;line-height:56px;">T</div>
    <h1 style="color:#fff;margin:0;font-size:22px;font-weight:900;">Tracy AI 小妹</h1>
    <p style="color:rgba(255,255,255,0.8);margin:6px 0 0;font-size:13px;">每週 AI 工具精選電子報 ✨</p>
  </div>

  <!-- Main Card -->
  <div style="background:#fff;border-radius:20px;padding:28px;margin-bottom:16px;border:1px solid #e8eaed;">
    <h2 style="color:#111827;font-size:18px;font-weight:800;margin:0 0 8px;">👋 嗨，訂閱者你好！</h2>
    <p style="color:#6b7280;font-size:14px;line-height:1.7;margin:0 0 20px;">
      這裡是 Tracy AI 小妹，本週為你精選了最值得關注的 AI 工具 🚀
    </p>

    <hr style="border:none;border-top:1px solid #f0f0f0;margin:20px 0;">

    <!-- Tool 1 -->
    <div style="margin-bottom:20px;">
      <div style="display:inline-block;background:rgba(168,85,247,0.1);color:#9333ea;padding:3px 10px;border-radius:100px;font-size:11px;font-weight:700;margin-bottom:8px;">🎬 本週推薦工具</div>
      <h3 style="color:#111827;font-size:16px;font-weight:700;margin:0 0 6px;">工具名稱</h3>
      <p style="color:#6b7280;font-size:13px;line-height:1.7;margin:0 0 12px;">
        在這裡填入工具的介紹與使用心得，讓訂閱者了解這個工具的亮點和適合的使用場景。
      </p>
      <a href="https://example.com" style="display:inline-block;background:linear-gradient(135deg,#a855f7,#f97316);color:#fff;text-decoration:none;padding:10px 20px;border-radius:12px;font-size:13px;font-weight:700;">
        前往試用 →
      </a>
    </div>

    <hr style="border:none;border-top:1px solid #f0f0f0;margin:20px 0;">

    <!-- 本週影片 -->
    <div style="background:#fdf4ff;border-radius:14px;padding:16px;">
      <p style="color:#9333ea;font-size:12px;font-weight:700;margin:0 0 6px;">📺 本週影片</p>
      <p style="color:#374151;font-size:14px;font-weight:600;margin:0 0 8px;">影片標題</p>
      <a href="https://youtube.com/@tracyai" style="color:#9333ea;font-size:13px;text-decoration:none;font-weight:600;">
        點此觀看 YouTube ▶
      </a>
    </div>
  </div>

  <!-- Footer -->
  <div style="text-align:center;padding:16px;">
    <p style="color:#9ca3af;font-size:12px;margin:0 0 4px;">
      您收到這封郵件是因為您訂閱了 Tracy AI 小妹電子報
    </p>
    <p style="color:#9ca3af;font-size:11px;margin:0;">
      © 2024 Tracy AI 小妹 · <a href="https://www.youtube.com/@tracyai" style="color:#a855f7;text-decoration:none;">YouTube 頻道</a>
    </p>
  </div>
</div>
</body></html>`;

// ===================== Admin Main =====================
const AdminPage: React.FC = () => {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('admin_auth'));
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [activeTab, setActiveTab] = useState<'send' | 'list'>('send');

  const fetchSubs = async () => {
    setLoadingSubs(true);
    try {
      const data = await getSubscribers();
      setSubscribers(data);
    } catch {
      toast.error('載入訂閱者失敗，請檢查 Supabase 設定');
    } finally {
      setLoadingSubs(false);
    }
  };

  useEffect(() => { if (authed) fetchSubs(); }, [authed]);

  const handleUnsubscribe = async (id: string) => {
    try {
      await unsubscribe(id);
      setSubscribers(prev => prev.filter(s => s.id !== id));
      toast.success('已移除該訂閱者');
    } catch {
      toast.error('操作失敗，請重試');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_auth');
    setAuthed(false);
  };

  if (!authed) return <LoginGate onLogin={() => setAuthed(true)} />;

  const tabs = [
    { key: 'send', label: '撰寫並發送', icon: <Send size={14} /> },
    { key: 'list', label: `訂閱者名單 (${subscribers.length})`, icon: <Users size={14} /> },
  ] as const;

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <a href="/" className="flex items-center gap-2.5 group">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black text-white"
                style={{ background: 'linear-gradient(135deg, #a855f7, #f97316)' }}>T</div>
              <span className="font-bold text-gray-800 group-hover:text-purple-600 transition-colors">Tracy AI 小妹</span>
            </a>
            <span className="text-gray-300 text-sm">/</span>
            <span className="text-sm font-semibold text-purple-600">電子報後台</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
              <Users size={12} className="text-purple-400" />
              <span className="font-mono font-bold text-gray-700">{subscribers.length}</span>
              <span>位訂閱者</span>
            </div>
            <button onClick={logout}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-500 transition-colors px-2 py-1.5 rounded-xl hover:bg-red-50">
              <LogOut size={13} /> 登出
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={springPresets.gentle}
          className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: <Users size={18} className="text-purple-500" />, value: subscribers.length, label: '總訂閱人數', color: '#9333ea', bg: 'rgba(168,85,247,0.08)' },
            { icon: <Mail size={18} className="text-orange-500" />, value: '∞', label: '可發送電子報', color: '#ea580c', bg: 'rgba(249,115,22,0.08)' },
            { icon: <Clock size={18} className="text-blue-500" />, value: subscribers.length > 0 ? new Date(subscribers[0]?.subscribed_at).toLocaleDateString('zh-TW') : '—', label: '最新訂閱', color: '#2563eb', bg: 'rgba(59,130,246,0.08)' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm shadow-gray-100/50">
              <div className="flex items-start justify-between">
                <div className="p-2 rounded-xl" style={{ background: stat.bg }}>{stat.icon}</div>
              </div>
              <p className="text-2xl font-black mt-3" style={{ color: stat.color, fontVariantNumeric: 'tabular-nums' }}>
                {stat.value}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Tab bar */}
        <div className="flex gap-2 mb-6 p-1 bg-gray-100/80 rounded-2xl w-fit">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: activeTab === tab.key ? '#fff' : 'transparent',
                color: activeTab === tab.key ? '#9333ea' : '#6b7280',
                boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              }}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={springPresets.snappy}>
            {activeTab === 'send' ? (
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm shadow-gray-100/50">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-black text-gray-900">撰寫電子報</h2>
                    <p className="text-sm text-gray-400 mt-0.5">支援 HTML 格式，可使用右側「套用範本」快速開始</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
                    <CheckCircle size={12} /> 系統正常
                  </div>
                </div>
                <EmailEditor subscribers={subscribers} disabled={loadingSubs} />
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm shadow-gray-100/50">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-black text-gray-900">訂閱者名單</h2>
                    <p className="text-sm text-gray-400 mt-0.5">共 {subscribers.length} 位有效訂閱者</p>
                  </div>
                  <button onClick={fetchSubs} disabled={loadingSubs}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-all border border-gray-200">
                    <RefreshCw size={13} className={loadingSubs ? 'animate-spin' : ''} /> 重新整理
                  </button>
                </div>
                <SubscriberTable subscribers={subscribers} onUnsubscribe={handleUnsubscribe} loading={loadingSubs} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default AdminPage;
